import { useEffect, useRef, useState } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Button, InputCheckbox, LoadingSpinner, SearchInput, InlineAlert } from '../components/BaseComponents'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import routes from '../components/Router/routes'
import { getAccountingPeriods } from '../redux/actions/accountingPeriodsActions'
import { useAppDispatch, useAppSelector } from '../redux/hooks/reduxHooks'
import {
	selectAccountingPeriods,
	selectAccountingPeriodsError,
	selectAccountingPeriodsStatus,
	selectAccountingPeriodsUpdateError,
	selectAccountingPeriodsUpdateStatus,
	ACCOUNTINGPERIODS_UPDATE_RESET,
} from '../redux/slices/accountingPeriodsSlice'
import { FetchStatus, PostStatus } from '../types/LoadingStates'
import { SortConfig } from '../components/BaseComponents/Datagrid/useSortableData'
import { centsToCurrency } from '../lib/utils'
import { updateItemsScope } from '../redux/actions/updateItemsActions'
import {
	selectUpdateItemsError,
	selectUpdateItemsStatus,
	UPDATEITEMS_RESET,
} from '../redux/slices/updateItemsSlice'
import { PurchaseUploadState } from '../enums/PurchaseUploadState'
import CanonicalModal from '../components/BaseComponents/CanonicalModal/CanonicalModal'
import {
	GET_PURCHASES_CARBON_ACCOUNTS_LIMIT,
} from '../redux/actions/carbonAccountsPurchasesActions'
import Pagination from '../components/BaseComponents/Pagination/Pagination'
import SimpleSortTable, {
	TableColumn,
} from '../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import { useDebouncedSearch } from '../hooks/useDebouncedSearch'
import { Scope2SelectionItem } from '../types/PurchaseCarbonData'
import { getScope2SelectionItems } from '../redux/actions/refinementsActions'
import { REFINEMENTS_SCOPE2_SELECTION_RESET, selectRefinementsScope2SelectionError, selectRefinementsScope2SelectionItems, selectRefinementsScope2SelectionStatus } from '../redux/slices/refinementsSlice'
import { PaginationMeta } from '../types/PaginationMeta'
import useCurrencyFormatter from '../hooks/useCurrencyFormatter'
import useApi from '../hooks/useApi'
import useCompleteAccountingStage from '../hooks/useCompleteAccountingStage'
import useRefinementRequest from '../hooks/useRefinementRequest'
import useInterruptibleRefinementSave from '../hooks/useInterruptibleRefinementSave'
import NumericText from '../components/BaseComponents/NumericText/NumericText'

export const CompanyRefineElectricity = () => {
	const formatCurrency = useCurrencyFormatter();
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const api = useApi();

	const { accountingPeriodId } = useParams() as { accountingPeriodId: string }

	const accountingPeriodsStatus = useAppSelector(selectAccountingPeriodsStatus)
	const accountingPeriodsError = useAppSelector(selectAccountingPeriodsError)
	const accountingPeriodUpdateStatus = useAppSelector(selectAccountingPeriodsUpdateStatus)
	const accountingPeriodUpdateError = useAppSelector(selectAccountingPeriodsUpdateError)
	const allAccountingPeriodData = useAppSelector(selectAccountingPeriods)
	const [errorNoPeriod, setErrorNoPeriod] = useState(false)

	const updateItemsStatus = useAppSelector(selectUpdateItemsStatus)
	const updateItemsError = useAppSelector(selectUpdateItemsError)

	const [searchKeyword, setSearchKeyword] = useState('')

	const [dirtyItems, setDirtyItems] = useState<{ id: string, name: string, selected: boolean }[]>([])
	const [confirmPurchaseModal, setConfirmPurchaseModal] = useState<boolean>(false)

	const limit = GET_PURCHASES_CARBON_ACCOUNTS_LIMIT
	const [orderBy, setOrderBy] = useState<string>('itemName')
	const [direction, setDirection] = useState<'asc' | 'desc'>('asc')
	const [companyId, setCompanyId] = useState('')
	const [accountingPeriodIdVal, setAccountingPeriodIdVal] = useState('')

	// Find the accounting period to match the url param
	const thisAccountingPeriod = allAccountingPeriodData.filter(
		(period) => period.id === accountingPeriodId
	)[0]

	const itemLoadStatus = useAppSelector(selectRefinementsScope2SelectionStatus)
	const itemError = useAppSelector(selectRefinementsScope2SelectionError)
	const itemData = useAppSelector(selectRefinementsScope2SelectionItems)
	const defaultPagination: PaginationMeta = {
		current_page: 1,
		first_page: 1,
		last_page: 1,
		per_page: 20,
		total: 0,
	}
	const { items, pagination } = itemData || { items: [], pagination: defaultPagination }
	const request = useRefinementRequest(api, accountingPeriodId)
	const startSave = useInterruptibleRefinementSave(api, accountingPeriodId)
	const lastQuery = useRef({ page: 1, orderBy: 'itemName', direction: 'asc' as 'asc' | 'desc', searchText: '' })
	const loadPurchases = (query: typeof lastQuery.current) => {
		lastQuery.current = query
		request.start((signal) => dispatch(getScope2SelectionItems(
			api, accountingPeriodId, query.page, query.orderBy, query.direction, limit, query.searchText, { signal }
		)))
	}
	const completeStage = useCompleteAccountingStage(PurchaseUploadState.scope2Refined, () => {
		dispatch(REFINEMENTS_SCOPE2_SELECTION_RESET())
		dispatch(UPDATEITEMS_RESET())
		dispatch(ACCOUNTINGPERIODS_UPDATE_RESET())
		navigate(generatePath(routes.companyRefineSuppliers.path, { accountingPeriodId }))
	})
	const canContinue = request.current && Boolean(thisAccountingPeriod) &&
		accountingPeriodsStatus === FetchStatus.loaded && itemLoadStatus === FetchStatus.loaded &&
		updateItemsStatus !== PostStatus.sending && updateItemsStatus !== PostStatus.sent &&
		accountingPeriodUpdateStatus !== PostStatus.sending && accountingPeriodUpdateStatus !== PostStatus.error
	const saveSelection = () => {
		const { signal, onSubmitted, finish } = startSave()
		const save = async () => {
			const saved = await dispatch(updateItemsScope(api, dirtyItems.map((item) => ({
				id: item.id,
				scope: item.selected ? 2 : null,
			})), { signal, onSubmitted }))
			finish()
			if (!signal.aborted) {
				setConfirmPurchaseModal(false)
				if (saved) completeStage(accountingPeriodId)
			}
		}
		save()
	}

	useEffect(() => {
		dispatch(ACCOUNTINGPERIODS_UPDATE_RESET())
		dispatch(UPDATEITEMS_RESET())
		setDirtyItems([])
		setConfirmPurchaseModal(false)
		setSearchKeyword('')
	}, [dispatch, api, accountingPeriodId])

	// Data loading
	useEffect(() => {
		// Fetch accounting periods if they aren't in the store, we need to compare against the IDs
		if (accountingPeriodsStatus === FetchStatus.initial) {
			dispatch(getAccountingPeriods(api))
		}

		// // Reset the purchases store if it's currently filled
		if (itemLoadStatus === FetchStatus.loaded) {
			dispatch({
				type: REFINEMENTS_SCOPE2_SELECTION_RESET,
			})
		}

		if (accountingPeriodsStatus === FetchStatus.loaded) {
			// If there's no accounting period defined, the url parameter is probably wrong, show an error
			if (typeof thisAccountingPeriod !== 'undefined') {
				setErrorNoPeriod(false)
				setCompanyId(thisAccountingPeriod.company_id)
				setAccountingPeriodIdVal(thisAccountingPeriod.id)

				loadPurchases({ page: 1, orderBy, direction, searchText: '' })
			} else {
				setErrorNoPeriod(true)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountingPeriodsStatus, api, accountingPeriodId])

	// DataGrid Columns
	const columns: TableColumn[] = [
		{
			id: 'selected',
			label: '',
			sortable: false,
			columnClass: 'w-3', // narrow fixed column width
			renderer: (columnKey, rowData) => {
				return (
					<InputCheckbox
						className='rounded border-charcoal-300 text-algae-600'
						disabled={!canContinue}
						value={rowData.name}
						checked={rowData.selected}
						onClick={(event: React.SyntheticEvent) => {
							// We don’t want the click event to bubble up through the
							// checkbox into the `tr` and trigger the table’s `rowClickHandler`
							event.stopPropagation()
						}}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							event.stopPropagation()
							const changed = dirtyItems.filter(item => item.id !== rowData._id)
							if(event.target.checked !== (rowData.scope === 2)) {
								changed.push(
									{ id: rowData._id, name: rowData.itemName, selected: event.target.checked},
								)
							}
							setDirtyItems(changed)
						}}
					/>
				)
			},
		},
		{ id: 'itemName', label: 'Product', sortable: true },
		{ id: 'supplierName', label: 'Supplier', sortable: true },
		{
			id: 'totalQuantity',
			label: 'Amount',
			sortable: true,
			renderer: (columnKey, rowData) => {
				return (
					<span className=' text-white '>
						<NumericText value={rowData.totalQuantity} /> {rowData.quantityUnit}
					</span>
				)
			},
		},
		{
			id: 'totalSpend',
			label: 'Total Spend',
			sortable: true,
			renderer: (columnKey, rowData) => {
				return <span className=' text-white '><NumericText value={formatCurrency(rowData.price)} /></span>
			},
		},
	]

	const defaultSorting: SortConfig = {
		key: 'itemName',
		direction: 'ascending',
	}

	// DataGrid data constructor
	const constructData = (dataSet: Scope2SelectionItem[]) => dataSet.map((item) => {
		const dirtyItem = dirtyItems.find(dirty => dirty.id === item.itemId)
		return {
			_id: item.itemId,
			itemName: item.itemName,
			price: centsToCurrency(item.totalSpend),
			scope: item.scope,
			supplierName: item.supplierName,
			totalQuantity: item.totalQuantity,
			quantityUnit: item.quantityUnit,
			selected: dirtyItem ? dirtyItem.selected : item.scope === 2,
		}
	})

	const runSearch = () => {
		if (companyId !== '' && accountingPeriodIdVal !== '') {
			loadPurchases({ page: 1, orderBy, direction, searchText: searchKeyword })
		}
	}
	const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchKeyword, runSearch)

	return (
		<PageTemplate>
			<Helmet>
				<title>{routes.companyRefineElectricity.name}</title>
			</Helmet>
			<PageHeadings
				crumbs={[
					{ label: 'Purchases', route: routes.companyPurchases.path },
					{ label: routes.companyAddAccountingPeriods.menuName as string, route: routes.companyAddAccountingPeriods.path },
					{ label: 'Refine scope 2 data - Emissions generated' },
				]}
			/>

			<>
				{/* Helpful information */}
				<div className='rounded-lg bg-sun-500 p-4 my-5'>
					<p className='mb-5'>Select the items that are electricity the organisation has bought.</p>
				</div>

				<div className='items-start'>
					<SearchInput
						name='filterPurchases'
						placeholder='Search By Product Name'
						value={searchKeyword}
						onChange={(event) => {
							setSearchKeyword(event.target.value)
						}}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								flushSearch()
							}
						}}
						onClear={() => {
							setSearchKeyword('')
							loadPurchases({ page: 1, orderBy, direction, searchText: '' })
						}}
						isLoading={searchInProgress}
						className='max-w-2xl'
					/>
				</div>

				{request.current && accountingPeriodsStatus !== FetchStatus.error && itemLoadStatus !== FetchStatus.error && <><SimpleSortTable
					isLoading={
						accountingPeriodsStatus === FetchStatus.loading ||
						itemLoadStatus === FetchStatus.loading
					}
					additionalClass='mt-4'
					data={constructData(items)}
					columns={columns}
					defaultSorting={defaultSorting}
					onSortPress={(key, direction) => {
						// Store current sorting values for pagination to reference in its API calls
						setOrderBy(key)
						setDirection(direction === 'ascending' ? 'asc' : 'desc')
						loadPurchases({
							page: 1, orderBy: key, direction: direction === 'ascending' ? 'asc' : 'desc', searchText: searchKeyword,
						})
					}}
				/>
				<Pagination
					isLoading={
						accountingPeriodsStatus === FetchStatus.loading ||
						itemLoadStatus === FetchStatus.loading
					}
					pagination={pagination}
					onPageChange={(newPageNumber) => {
						loadPurchases({ page: newPageNumber, orderBy, direction, searchText: searchKeyword })
					}}
				/></>}

				<div className='rounded-lg bg-charcoal-600 p-2 mt-5'>
					<Button
						type='Primary'
						text='Next Step'
						hasTrailingIcon
						icon='fa-solid fa-chevron-right'
						additionalClass='float-right'
						disabled={!canContinue}
						onClick={() => {
							setConfirmPurchaseModal(!confirmPurchaseModal)
						}}
					/>
				</div>

				<CanonicalModal
					open={confirmPurchaseModal}
					onClose={() => setConfirmPurchaseModal(false)}
					size='small'
					title='Confirm scope 2 selections'
					actions={
						<>
							<Button
								hasLeadingIcon
								icon='fa-solid fa-plus'
								type='Outlined'
								text='Add a purchase'
								additionalClass='mr-5'
								onClick={() => {
									setSearchKeyword('')
									setConfirmPurchaseModal(false)
								}}
							/>
							<Button
								hasTrailingIcon
								icon='fa-solid fa-chevron-right'
								disabled={!canContinue}
								onClick={() => {
									// Dispatch data to item scope update endpoint

									if (dirtyItems.length > 0) {
										saveSelection()
									} else {
										setConfirmPurchaseModal(false)
										completeStage(accountingPeriodId)
									}
								}}
								type='Primary'
								text='Confirm selection'
							/>
						</>
					}
				>
					<div className='max-w-2xl'>
						{!dirtyItems.some(item => item.selected) ? (
							<label className='block text-white pt-6 pb-4'>
								Are you sure? You haven't selected any scope 2 purchases.
							</label>
						) : (
							<>
								<label className='block text-white pt-6 pb-4'>
									Have you selected all your scope 2 purchases?
								</label>
								<table className='table-fixed w-full py-2 divide-y-1 divide-charcoal-050'>
									<thead className='bg-charcoal-800 border-t-2 border-b-2 border-charcoal-050'>
										<tr>
											<th
												scope='col'
												className='p-lg text-left text-sm text-white leading-5'
											>
												Selected Products
											</th>
										</tr>
									</thead>
									<tbody className='divide-y divide-charcoal-050'>
										{dirtyItems.filter(item => item.selected).map((item) => {
											return (
												<tr key={item.id}>
													<td className='truncate p-lg text-sm text-white'>
														{item.name}
													</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</>
						)}
					</div>
				</CanonicalModal>
			</>

			{/* Errors and loading states*/}
			{accountingPeriodsStatus === FetchStatus.loading && (
				<LoadingSpinner loadingText='Loading accounting periods...' />
			)}
			{(updateItemsStatus === PostStatus.sending || accountingPeriodUpdateStatus === PostStatus.sending) && (
				<LoadingSpinner loadingText='Sending items...' />
			)}
			{accountingPeriodsStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching the accounting periods list. If this problem persists,
						please contact the Neoni team.
					</p>
					<p className='text-white'>{accountingPeriodsError}</p>
					<Button type='Primary' text='Retry loading accounting periods' onClick={() => dispatch(getAccountingPeriods(api))} />
				</InlineAlert>
			)}
			{request.current && itemLoadStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching the purchases for this accounting period (
						{accountingPeriodId}). If this problem persists, please contact the Neoni team.
					</p>
					<p className='text-white'>{itemError}</p>
					<Button type='Primary' text='Retry loading purchases' onClick={() => loadPurchases(lastQuery.current)} />
				</InlineAlert>
			)}
			{updateItemsStatus === PostStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error updating those items' scope. If this problem persists, please contact
						the Neoni team.
					</p>
					<p className='text-white mb-5'>{updateItemsError}</p>
					<Button
						type='Primary'
						text='Retry'
						hasTrailingIcon
						icon='fa-solid fa-rotate-right'
						onClick={() => {
							// Try another dispatch
							if (dirtyItems.length > 0) {
								saveSelection()
							} else {
								completeStage(accountingPeriodId)
							}
						}}
					/>
				</InlineAlert>
			)}
			{accountingPeriodUpdateStatus === PostStatus.error && (
				<InlineAlert type='error' className='mt-4'>
					<p className='text-white'>Your selections were saved, but the refinement stage could not be completed.</p>
					<p className='text-white'>{accountingPeriodUpdateError}</p>
					<Button type='Primary' text='Retry completion' onClick={() => completeStage(accountingPeriodId)} />
				</InlineAlert>
			)}
			{errorNoPeriod && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error whilst trying to find purchases for that accounting period ID (
						{accountingPeriodId}). If this problem persists, please contact the Neoni team.
					</p>
					<Button
						onClick={() => {
							navigate(
								generatePath(
									routes.companyRefineEmissionsGenerated.path,
									{ accountingPeriodId}
								),
								{replace: true}
							)
						}}
						type='Primary'
						hasTrailingIcon={true}
						icon='fa-solid fa-rotate-right'
						text='Try again'
						additionalClass='mt-9 mr-5'
					/>
					<Button
						type='Outlined'
						text='Back to accounting periods'
						onClick={() => {
							navigate(routes.companyAddAccountingPeriods.path)
						}}
						additionalClass='mt-6 mr-5'
					/>
				</InlineAlert>
			)}
		</PageTemplate>
	)
}

export default CompanyRefineElectricity
